import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchPolygonSnapshotPrice } from '../composables/fetchPolygon';
import ToggleCurrency from "./ToggleCurrency";
import { useExchangeRates } from '../context/ExchangeRateContext'; // นำเข้า useExchangeRates
import { usePortfolio } from '../context/PortfolioContext';
import { useMarketPrices } from '../context/MarketPriceContext';
import { getPortfolioThemeStyle } from '../utils/portfolioTheme'; // ปรับ Path ตามโครงสร้างโปรเจกต์ของคุณ
import { isCryptoPortfolio } from '../utils/portfolioType';
import { num } from '../utils/cryptoDecimal';

interface PortfolioSummary {
  name: string;
  totalValue: number;
  profit: number;
  percent: number;
  portId: string;
  stockMetadata?: {
    totalValue: number;
  };
  subPorts?: any[];
  stocks?: any[];
}

interface PortfolioSummaryListProps {
  // portfolios: PortfolioSummary[];
  // currentPrices: { [symbol: string]: number }; // รับ currentPrices จาก HomePage

}

const PortfolioSkeleton = () => (
  <div className="rounded-xl p-3 flex flex-col border border-gray-800 bg-gray-900/50 animate-pulse">
    <div className="flex justify-between items-start mb-2">
      <div className="flex flex-col gap-2">
        <div className="h-5 w-20 bg-gray-700 rounded"></div>
        <div className="h-4 w-12 bg-gray-800 rounded"></div>
      </div>
      <div className="flex flex-col items-end gap-1">
        <div className="h-4 w-12 bg-gray-700 rounded"></div>
        <div className="h-4 w-26 bg-gray-800 rounded"></div>
      </div>
    </div>
    <div className="h-6 w-28 bg-gray-700 rounded mt-2"></div>
  </div>
);

const getBgColor = (profit: number) => {
  if (profit > 0) return 'bg-pink-100';
  if (profit < 0) return 'bg-yellow-100';
  return 'bg-blue-100';
};

const getProfitColor = (profit: number) => {
  return profit >= 0 ? 'text-green-600' : 'text-red-600';
};

function getPortfolioValue(portfolio: any, currentPrices: { [symbol: string]: number }) {
  if (!portfolio.stocks) return 0;
  return portfolio.stocks.reduce((sum: number, stock: any) => {
    const price = currentPrices[stock.stockSymbol] ?? 0;
    return sum + stock.quantity * price;
  }, 0);
}

const PortfolioSummaryList: React.FC<PortfolioSummaryListProps> = () => {
  const { userPortfolios: portfolios, displayMode, loading } = usePortfolio();
  const navigate = useNavigate();
  const { currentPrices, fetchPricesForSymbols } = useMarketPrices();
  // const [currentPrices, setCurrentPrices] = useState<{ [symbol: string]: number }>({});
  // const [loading, setLoading] = useState(true);
  const [currency, setCurrency] = useState<string>(() => {
    // ดึงค่า currency จาก localStorage ถ้ามี
    return localStorage.getItem("selectedCurrency") || "USD";
  });
  const { exchangeRates } = useExchangeRates(); // ใช้ exchangeRates จาก Context
  const [includeCash, setIncludeCash] = useState(() => {
    const saved = localStorage.getItem('includeCash');
    return saved ? JSON.parse(saved) : true; // default เป็น true
  });
  useEffect(() => {
    const handleStorageChange = () => {
      const saved = localStorage.getItem('includeCash');
      if (saved !== null) setIncludeCash(JSON.parse(saved));
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);
  // useEffect(() => {
  //   async function fetchAllPrices() {
  //     setLoading(true);
  //     const prices: { [symbol: string]: number } = {};
  //     for (const portfolio of portfolios) {
  //       for (const subPort of portfolio.subPorts || []) {
  //         for (const stock of subPort.stocks || []) {
  //           if (!prices[stock.stockSymbol]) {
  //             const price = await fetchPolygonSnapshotPrice(stock.stockSymbol);
  //             if (price) prices[stock.stockSymbol] = price;
  //           }
  //         }
  //       }
  //     }
  //     setCurrentPrices(prices);
  //     setLoading(false);
  //   }
  //   if (portfolios.length > 0) {
  //     fetchAllPrices();
  //   } else {
  //     setLoading(false);
  //   }
  // }, [portfolios]);
  useEffect(() => {
    const allSymbols = new Set<string>();
    portfolios.forEach(port => {
      (port.subPorts || []).forEach((sub: any) => {
        const items = sub.stocks || sub.holdings || [];
        items.forEach((item: any) => {
          const symbol = item.stockSymbol || item.assetSymbol || item.symbol;
          if (symbol) allSymbols.add(symbol);
        });
      });
    });

    if (allSymbols.size > 0) {
      fetchPricesForSymbols(Array.from(allSymbols));
    }
  }, [portfolios, fetchPricesForSymbols]);
  return (
    <div
      className="w-full rounded-xl"
      style={{
        backgroundColor: 'var(--component-bg-color)',
        maxHeight: '385px', // ขยายความสูงให้เห็นข้อมูลชัดขึ้น
        overflowY: 'auto',
      }}
    >
      {/* ส่วน Header ที่ปรับใหม่ */}
      <div
        className="sticky top-0 z-9 pt-4 px-4 pb-2 mb-2 border-b"
        style={{
          backgroundColor: 'var(--component-bg-color)',
          borderColor: 'var(--text-input)'
        }}
      >
        <div className="flex justify-between items-start">
          {/* ฝั่งซ้าย: Title และ Exchange Rate Info */}
          <div className="flex flex-col gap-1">
            <h2
              className="text-base font-bold m-0"
              style={{ color: 'var(--text-color)' }}
            >
              พอร์ตหุ้นทั้งหมด
            </h2>
            {/* แสดงอัตราแลกเปลี่ยนขนาดเล็กเพื่อให้ดูเป็นข้อมูลเสริม */}
            {/* <div className="flex flex-col opacity-60">
          <span className="text-[10px]" style={{ color: 'var(--text-color)' }}>
            1 USD = {exchangeRates["THB"]?.toFixed(2) || "31.20"} THB
          </span>
          <span className="text-[9px]" style={{ color: 'var(--secondary-text-color)' }}>
            อัปเดตเมื่อ: 2026-02-23
          </span>
        </div> */}
          </div>

          {/* ฝั่งขวา: Toggle Switch */}
          <div className="pt-1">
            <ToggleCurrency
              selectedCurrency={currency}
              onToggle={(newCurrency) => {
                setCurrency(newCurrency);
                localStorage.setItem("selectedCurrency", newCurrency);
              }}
            />
          </div>
        </div>
      </div>
      <div
        className="flex flex-col gap-2 mb-4 px-4"
        style={{
          scrollbarWidth: 'none',
        }}
      >
        {loading ? (
          <>
            <PortfolioSkeleton />
            <PortfolioSkeleton />
            <PortfolioSkeleton />
          </>

        ) : portfolios.length === 0 && !loading ? (
          // เพิ่มส่วนแสดงผลเมื่อไม่มีพอร์ต
          <div className="flex flex-col items-center justify-center py-10 text-center">
            <p style={{ color: 'var(--secondary-text-color)' }}>
              คุณยังไม่มีพอร์ตในโหมดนี้
            </p>
          </div>
        ) : (
          <>
            {portfolios.map((port) => {
              const subPorts = port.subPorts || [];

              let totalAssetValueUSD = 0;
              let totalAssetCostUSD = 0;

              // ✅ 1. เพิ่มการดึง Realized Gain จากข้อมูลพอร์ต (อิงตามโครงสร้าง JSON เดิมของคุณ)
              // พอร์ตคริปโต: ค่าตัวเลขมาเป็น string และแสดงเป็น USD เสมอ (ไม่ตามปุ่ม THB)
              const isCrypto = isCryptoPortfolio(port);
              const cardCurrency = isCrypto ? 'USD' : currency;
              const realizedGainUSD = num(port.summary?.realizedGain || port.manualStockRealizedGain || 0);

              subPorts.forEach((sub: any) => {
                const items = sub.stocks || sub.holdings || [];
                items.forEach((item: any) => {
                  const symbol = item.stockSymbol || item.assetSymbol || item.symbol;
                  const quantity = num(item.quantity);
                  const price = num(currentPrices[symbol]);
                  const avgPrice = num(item.avgBuyPrice || item.avgEntryPrice || 0);

                  totalAssetValueUSD += quantity * price;
                  totalAssetCostUSD += quantity * avgPrice;
                });
              });

              const cashValueUSD = num(port.cashBalances?.[0]?.amount || port.cash?.cash || 0);
              const exchangeRate = cardCurrency === "THB" ? (exchangeRates["THB"] || 1) : 1;

              // ✅ 2. คำนวณ Profit แบบรวม Realized Gain
              // Unrealized (ที่ยังไม่ขาย) = totalAssetValueUSD - totalAssetCostUSD
              const unrealizedProfit = (totalAssetValueUSD - totalAssetCostUSD) * exchangeRate;
              const realizedGainConverted = realizedGainUSD * exchangeRate;

              // สรุปกำไร: รวม Realized Gain เข้าไปด้วยเพื่อให้ตรงกับภาพรวมพอร์ต
              const totalProfit = unrealizedProfit + realizedGainConverted;

              const totalValue = includeCash
                ? (totalAssetValueUSD + cashValueUSD) * exchangeRate
                : totalAssetValueUSD * exchangeRate;
              // ต้นทุนรวม
              const totalCost = (totalAssetCostUSD) * exchangeRate;

              // % กำไร
              const percent = totalCost > 0 ? (totalProfit / totalCost) * 100 : 0;
              const customThemeStyle = getPortfolioThemeStyle(port.settings?.themeConfig);
              return (
                <div
                  key={port.portfolioId || port.name || port.portId}
                  className={isCrypto
                    // กรอบไล่สีส้ม-ม่วงแบบเดียวกับการ์ดคริปโตในหน้าพอร์ตรวม (.crypto-port-card ใน App.css)
                    ? "crypto-port-card p-3 pb-1 flex flex-col hover:brightness-110 transition-all duration-300 cursor-pointer"
                    : "rounded-xl p-3 pb-1 flex flex-col hover:bg-[var(--hover-item)] transition-all duration-300 cursor-pointer border"}
                  style={isCrypto ? undefined : {
                   backgroundColor: customThemeStyle.backgroundColor,
                    borderColor: customThemeStyle.borderColor,
                    boxShadow: customThemeStyle.boxShadow,
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/portfolio/${port.portfolioId || port.portId}`, { state: { portfolioName: port.name } });
                  }}
                >
                  {/* UPPER SECTION */}
                  <div className="flex justify-between items-start">
                    <div className="flex flex-col">
                      <span className="text-lg font-bold leading-none" style={{ color: 'var(--text-color)' }}>
                        {port.name}
                      </span>
                      <span className="text-sm opacity-80" style={{ color: 'var(--text-color)' }}>
                        มูลค่า:
                      </span>
                    </div>

                    <div className="flex flex-col items-end">
                      <span
                        className="text-xs font-bold flex items-center gap-1"
                        style={{ color: totalProfit >= 0 ? 'var(--green-text-color)' : 'var(--red-text-color)' }}
                      >
                        {totalProfit >= 0 ? 'กำไร 😚' : 'ขาดทุน 😣'}
                      </span>
                      <span
                        className="text-xs font-medium"
                        style={{ color: totalProfit >= 0 ? 'var(--green-text-color)' : 'var(--red-text-color)' }}
                      >
                        ({percent >= 0 ? '+' : ''}{percent.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%)
                      </span>
                      <span
                        className="text-xs font-bold"
                        style={{ color: totalProfit >= 0 ? 'var(--green-text-color)' : 'var(--red-text-color)' }}
                      >
                        {cardCurrency === "THB" ? "฿" : "$"}{Math.abs(totalProfit).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>

                  {/* LOWER SECTION */}
                  <div className="flex justify-between items-end">
                    <span className="text-base font-bold tracking-tight" style={{ color: 'var(--text-color)' }}>
                      {cardCurrency === "THB" ? "฿" : "$"}{totalValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>
              );
            })}
          </>)

        }
      </div>
      <style>
        {`
          div::-webkit-scrollbar {
            display: none;
          }
        `}
      </style>
    </div>
  );
};

export default PortfolioSummaryList;